import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
} from '@/components/ui/select';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface FilterOption {
	type: string;
	label: string;
	options: Array<{ value: string; label: string }>;
}

export interface SelectedFilter {
	type: string;
	value: string;
	displayLabel?: string;
}

interface FilterProps {
	filters: Array<FilterOption>;
	onFilterChange?: (filters: SelectedFilter[]) => void;
}

/** Valor interno do Select para "Todos" — evita string vazia no componente. */
const FILTER_ALL_VALUE = '__all__';

const Filter = ({ filters, onFilterChange }: FilterProps) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [selectedFilters, setSelectedFilters] = useState<
		Array<SelectedFilter>
	>([]);
	const [selectValues, setSelectValues] = useState<{ [key: string]: string }>(
		{},
	);

	useEffect(() => {
		const initialFilters: SelectedFilter[] = [];
		const initialSelectValues: { [key: string]: string } = {};

		filters.forEach((filter) => {
			const paramValue = searchParams.get(filter.type);
			if (paramValue) {
				const option = filter.options.find(
					(opt) => opt.value === paramValue,
				);
				if (option) {
					initialFilters.push({
						type: filter.type,
						value: paramValue,
						displayLabel: option.label,
					});
					initialSelectValues[filter.type] = paramValue;
				}
			}
		});

		setSelectedFilters(initialFilters);
		setSelectValues(initialSelectValues);
	}, [filters, searchParams]);

	const handleFilterChange = (type: string, value: string) => {
		const filterOption = filters.find((f) => f.type === type);
		const option = filterOption?.options.find((opt) => opt.value === value);

		const newParams = new URLSearchParams(searchParams.toString());

		if (value === '' || value === FILTER_ALL_VALUE) {
			setSelectedFilters((prev) =>
				prev.filter((filter) => filter.type !== type),
			);
			setSelectValues((prev) => ({ ...prev, [type]: '' }));
			newParams.delete(type);
			setSearchParams(newParams, { replace: true });
			if (onFilterChange)
				onFilterChange(selectedFilters.filter((f) => f.type !== type));
			return;
		}

		const newFilters = [
			...selectedFilters.filter((filter) => filter.type !== type),
			{ type, value, displayLabel: option?.label },
		];
		setSelectedFilters(newFilters);
		setSelectValues((prev) => ({ ...prev, [type]: value }));
		newParams.set(type, value);
		setSearchParams(newParams, { replace: true });
		if (onFilterChange) onFilterChange(newFilters);
	};

	const Label = ({ children }: { children: React.ReactNode }) => (
		<label className="mb-2 block text-[11px] font-bold uppercase tracking-wider text-slate-500">
			{children}
		</label>
	);

	return (
		<div className="grid grid-cols-1 gap-x-6 gap-y-6 md:grid-cols-2 lg:grid-cols-3">
			{/* Filtros Dinâmicos baseados no array 'filters' */}
			{filters.map((filter) => (
				<div key={filter.type} className="flex flex-col">
					<Label>{filter.label}</Label>
					<Select
						value={selectValues[filter.type] || FILTER_ALL_VALUE}
						onValueChange={(value) =>
							handleFilterChange(filter.type, value)
						}
					>
						<SelectTrigger
							className="h-11 rounded-xl border-stone-200 bg-white font-semibold text-zinc-600 shadow-sm"
							hideTriggerIcon={true}
						>
							<span
								className={cn(
									!selectValues[filter.type] &&
										'font-normal text-zinc-400',
								)}
							>
								{selectValues[filter.type]
									? filter.options.find(
											(o) =>
												o.value ===
												selectValues[filter.type],
									  )?.label
									: 'Todos'}
							</span>
							<ChevronDown className="h-4 w-4 opacity-50" />
						</SelectTrigger>
						<SelectContent className="bg-white">
							<SelectItem
								key="__all__"
								value={FILTER_ALL_VALUE}
								indicator={false}
							>
								Todos
							</SelectItem>
							{filter.options.map((option) => (
								<SelectItem
									key={option.value}
									value={option.value}
									indicator={false}
								>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			))}
		</div>
	);
};

export { Filter };
